import type { Job } from '@uroute/shared';
import type { PillTone } from '@uroute/ui';
import { isDelivered } from './jobStatus';

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "Wed 14 Oct" ??? hero header date. */
export function formatDayHeading(d: Date = new Date()): string {
  return `${DAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "12:00" / "14 Oct" depending on how far away the moment is. */
export function formatClockOrDate(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  if (d.toDateString() === new Date(now).toDateString()) return formatTime(iso);
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${formatTime(iso)}`;
}

export function formatDuration(ms: number): string {
  const mins = Math.floor(Math.abs(ms) / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const restMins = mins % 60;
  if (hours < 24) return restMins ? `${hours}h ${restMins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
}

/** "12:00" today or "Wed 15 12:00" ??? absolute deadline labels. */
export function formatMoment(ts: number, now = Date.now()): string {
  const d = new Date(ts);
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (d.toDateString() === new Date(now).toDateString()) return time;
  return `${DAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${time}`;
}

export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const diff = now - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return formatClockOrDate(iso, now);
}

export function isSameLocalDay(iso: string | null | undefined, ref = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

export function endOfLocalDay(ref = new Date()): number {
  const d = new Date(ref);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

/* ------------------------------ urgency model ----------------------------- */

export type UrgencyTone = PillTone;

export interface DeadlineInfo {
  tone: UrgencyTone;
  /** Short primary label, e.g. "Due in 42m". */
  label: string;
  /** Secondary context, e.g. the absolute time. */
  sub: string;
  overdue: boolean;
  /** ms until the commitment (negative = late), null when there is none. */
  msLeft: number | null;
}

export type Deadlineish = Pick<Job, 'deadlineAt' | 'timeWindowStart' | 'timeWindowEnd' | 'status'>;

/** When must this delivery be done by? Window end wins over a bare deadline. */
export function commitmentTime(job: Deadlineish): number | null {
  const iso = job.timeWindowEnd ?? job.deadlineAt ?? job.timeWindowStart;
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

export function deadlineInfo(job: Deadlineish, now = Date.now()): DeadlineInfo {
  const sub = windowLabel(job);
  const at = commitmentTime(job);
  if (at === null) {
    return { tone: 'neutral', label: 'No deadline', sub, overdue: false, msLeft: null };
  }
  if (isDelivered(job.status)) {
    return { tone: 'success', label: 'Delivered', sub, overdue: false, msLeft: 0 };
  }
  const msLeft = at - now;
  if (msLeft < 0) {
    return {
      tone: 'danger',
      label: `Overdue — ${formatDuration(msLeft)} late`,
      sub,
      overdue: true,
      msLeft,
    };
  }
  if (msLeft <= 60 * 60_000) {
    return { tone: 'warning', label: `Due in ${formatDuration(msLeft)}`, sub, overdue: false, msLeft };
  }
  if (msLeft <= 6 * 60 * 60_000) {
    return { tone: 'info', label: `Due in ${formatDuration(msLeft)}`, sub, overdue: false, msLeft };
  }
  return { tone: 'neutral', label: `Due ${formatMoment(at, now)}`, sub, overdue: false, msLeft };
}

/** "10:00–12:00", "by 12:00", or "—". */
export function windowLabel(job: Deadlineish): string {
  if (job.timeWindowStart && job.timeWindowEnd) {
    return `${formatTime(job.timeWindowStart)}–${formatTime(job.timeWindowEnd)}`;
  }
  if (job.deadlineAt) return `by ${formatClockOrDate(job.deadlineAt)}`;
  if (job.timeWindowStart) return `from ${formatClockOrDate(job.timeWindowStart)}`;
  return '—';
}

/** Single-line address: "Acme Foods DC, London E16 1AA". */
export function addressLine(line1: string, city: string, postcode: string): string {
  return [line1, city, postcode].filter(Boolean).join(', ');
}

export function mapsUrl(dest: {
  line1: string;
  line2?: string | null;
  city: string;
  postcode: string;
  latitude?: number | null;
  longitude?: number | null;
}): string {
  const q =
    dest.latitude != null && dest.longitude != null
      ? `${dest.latitude},${dest.longitude}`
      : [dest.line1, dest.line2, dest.city, dest.postcode].filter(Boolean).join(', ');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}&travelmode=driving`;
}

export function telUrl(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
